import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '@prooffleet/client';

interface UnitSummary {
  _id: string;
  unitNumber: string;
  kind: string;
  make: string | null;
  model: string | null;
}

/** BarcodeDetector есть не везде (Safari) — сканер включаем только при поддержке. */
function barcodeDetectorAvailable(): boolean {
  return 'BarcodeDetector' in window;
}

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike;

export function UnitSelect(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const { data } = useQuery({
    queryKey: ['units'],
    queryFn: () => api<{ units: UnitSummary[] }>('/units'),
  });

  const stopScan = (): void => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  };

  useEffect(() => stopScan, []);

  const resolveToken = async (token: string): Promise<void> => {
    try {
      const result = await api<{ unit: UnitSummary }>(`/units/by-qr/${token}`);
      stopScan();
      void navigate(`/units/${result.unit._id}/briefing`);
    } catch {
      setError(t('unit.notRecognized'));
    }
  };

  const startScan = async (): Promise<void> => {
    setError(null);
    if (!barcodeDetectorAvailable()) {
      setError(t('unit.notRecognized'));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setScanning(true);

      const video = videoRef.current;
      if (video === null) return;
      video.srcObject = stream;
      await video.play();

      const Ctor = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector;
      const detector = new Ctor({ formats: ['qr_code'] });

      const tick = async (): Promise<void> => {
        if (streamRef.current === null) return;
        try {
          const codes = await detector.detect(video);
          const first = codes[0];
          if (first !== undefined) {
            // Наклейка может содержать полный URL — берём последний сегмент.
            const token = first.rawValue.split('/').filter(Boolean).pop() ?? first.rawValue;
            await resolveToken(token);
            return;
          }
        } catch {
          // Кадр не распознан — продолжаем.
        }
        requestAnimationFrame(() => void tick());
      };

      void tick();
    } catch {
      setError(t('unit.notRecognized'));
      stopScan();
    }
  };

  const units = (data?.units ?? []).filter((unit) =>
    unit.unitNumber.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="md" maw={640} mx="auto" w="100%">
          <Title order={1}>{t('unit.title')}</Title>

          {scanning && (
            <Card p={0} style={{ overflow: 'hidden' }}>
              <video ref={videoRef} playsInline muted style={{ width: '100%', display: 'block' }} />
            </Card>
          )}

          {error !== null && <Alert color="red">{error}</Alert>}

          <TextInput
            label={t('unit.search')}
            inputMode="text"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
          />

          <Stack gap="sm">
            {units.length === 0 && <Text c="dimmed">{t('unit.empty')}</Text>}
            {units.map((unit) => (
              <Card
                key={unit._id}
                component="button"
                onClick={() => void navigate(`/units/${unit._id}/briefing`)}
                style={{ textAlign: 'left', cursor: 'pointer' }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text fw={700} fz="xl">
                      {unit.unitNumber}
                    </Text>
                    <Text c="dimmed">
                      {[unit.make, unit.model].filter(Boolean).join(' ') || unit.kind}
                    </Text>
                  </div>
                </Group>
              </Card>
            ))}
          </Stack>
        </Stack>
      </div>

      <div className="pf-action-bar">
        {scanning ? (
          <Button fullWidth variant="light" onClick={stopScan}>
            {t('common.cancel')}
          </Button>
        ) : (
          <Button fullWidth onClick={() => void startScan()}>
            {t('unit.scan')}
          </Button>
        )}
        {!scanning && (
          <Button
            fullWidth
            variant="subtle"
            mt="xs"
            onClick={() => void navigate('/history')}
          >
            {t('history.link')}
          </Button>
        )}
      </div>
    </div>
  );
}
